import { CheckCircle, XCircle, Shield, Flame, Star, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import fenixBook from "@/assets/fenix-financiero-book.png";
import bonusBancosQuiebra from "@/assets/bonus-bancos-quiebra.png";
import bonusLimpiezaRastro from "@/assets/bonus-limpieza-rastro.png";
import bonusEscudoLegal from "@/assets/bonus-escudo-legal.png";
import { useEffect } from "react";
import { useHotmartIframeFix } from "@/hooks/use-hotmart-iframe-fix";
import LazyVideo from "@/components/landing/LazyVideo";

const Downsell = () => {
  useHotmartIframeFix("hotmart-sales-funnel-downsell", {
    minHeightDesktop: 260,
    maxHeightDesktop: 300,
    viewportFillDesktop: 0.28,
    minHeightMobile: 260,
    maxHeightMobile: 300,
    viewportFillMobile: 0.32,
  });

  useEffect(() => {
    // Load Hotmart checkout script
    const script = document.createElement('script');
    script.src = 'https://checkout.hotmart.com/lib/hotmart-checkout-elements.js';
    script.async = true;
    script.onload = () => {
      if ((window as any).checkoutElements) {
        (window as any).checkoutElements.init('salesFunnel').mount('#hotmart-sales-funnel-downsell');
      }
    };
    document.body.appendChild(script);
    return () => {
      const existingScript = document.querySelector('script[src="https://checkout.hotmart.com/lib/hotmart-checkout-elements.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);
  const testimonials = [{
    name: "Roberto M.",
    text: "Compré mi casa 14 meses después de la bancarrota. Este programa me enseñó exactamente cómo hacerlo.",
    location: "Houston, TX"
  }, {
    name: "María Elena S.",
    text: "Pensé que nunca volvería a tener crédito. Hoy tengo 3 tarjetas y un auto nuevo.",
    location: "Miami, FL"
  }, {
    name: "Carlos V.",
    text: "De 480 puntos a 720 en solo 18 meses post-bancarrota. Increíble.",
    location: "Phoenix, AZ"
  }];
  return <main className="min-h-screen bg-gradient-to-b from-white via-orange-50/30 to-white text-gray-900">
      {/* Top Urgency Bar */}
      <div className="bg-gradient-to-r from-teal-500 via-cyan-400 to-amber-500 py-3 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBIMHoiLz48cGF0aCBkPSJNMjAgMjBtLTEgMGExIDEgMCAxIDAgMiAwYTEgMSAwIDEgMCAtMiAwIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiLz48L2c+PC9zdmc+')] opacity-30"></div>
        <p className="md:text-lg relative z-10 text-slate-900 font-extrabold text-xl">
          🔥 ÚLTIMO PASO: OPORTUNIDAD DE RENACIMIENTO FINANCIERO
        </p>
        {/* Progress Bar */}
        <div className="max-w-xs mx-auto mt-2 relative z-10">
          <div className="h-2 bg-black/20 rounded-full overflow-hidden">
            <div className="h-full w-[90%] bg-gradient-to-r from-teal-300 to-amber-300 rounded-full"></div>
          </div>
          <p className="text-xs mt-1 text-slate-800">90% Completado</p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Pre-title */}
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full mb-6">
            <Flame className="w-4 h-4" />
            <span className="font-medium text-xl">RENACE DE LAS CENIZAS</span>
          </div>
          
          {/* Main Title */}
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight">
            <span className="text-gray-800">
              ¿LA BANCARROTA TE HA MARCADO?
            </span>
            <br />
            <span className="bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 bg-clip-text text-transparent">
              ELIMINA EL ESTIGMA HOY
            </span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            El protocolo paso a paso para obtener <strong className="text-gray-900">tarjetas, autos y casa</strong> solo{" "}
            <strong className="text-orange-600">12 meses después del colapso</strong>.
          </p>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-8 px-4">
        <div className="max-w-sm mx-auto">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-orange-300/50">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 to-blue-400/20 blur-xl -z-10"></div>
            <LazyVideo 
              className="w-full aspect-[9/16]" 
              poster="/images/video-poster-downsell.jpg"
              src="/videos/video-downsell.mp4"
            />
          </div>
        </div>
      </section>

      {/* Product Image Section */}
      <section className="py-8 px-4">
        <div className="max-w-lg mx-auto">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-blue-400 blur-3xl opacity-20 rounded-full"></div>
            <img alt="Fénix Financiero - Guía Digital" className="relative w-full max-w-sm mx-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300 shadow-md" src="/lovable-uploads/cf76a635-b90b-46e3-853a-25038077bc60.png" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>

      {/* Problem vs Solution Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Problem Column */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Sin Fénix Financiero</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Miedo constante al rechazo de crédito</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Intereses criminales del 25%+ APR</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Vergüenza y estigma social por años</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Sin acceso a vivienda ni vehículos</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">7-10 años esperando que "desaparezca"</span>
                </li>
              </ul>
            </div>

            {/* Solution Column */}
            <div className="bg-gradient-to-br from-orange-50 to-blue-50 rounded-2xl p-8 border-2 border-orange-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-orange-200/30 rounded-full blur-2xl"></div>
              <div className="flex items-center gap-3 mb-6 relative z-10">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center">
                  <Flame className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Con Fénix Financiero</h3>
              </div>
              <ul className="space-y-4 relative z-10">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Aprobación rápida</strong> en bancos específicos</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Limpieza de registros públicos</strong> legalmente</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Estrategia de tarjetas aseguradas</strong> que funcionan</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Préstamos auto</strong> con tasas razonables</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Casa propia</strong> en 12-24 meses</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Bonus Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-teal-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-400/20 rounded-full blur-2xl"></div>
            
            <div className="text-center mb-8 relative z-10">
              <span className="inline-block bg-gradient-to-r from-teal-400 via-cyan-400 to-amber-400 text-slate-900 text-xs font-bold px-4 py-1 rounded-full mb-4">
                🎁 REGALOS ESPECIALES
              </span>
              <h3 className="text-2xl md:text-3xl font-bold">
                REGALOS POR AGREGAR ESTO AHORA
              </h3>
            </div>

            <div className="grid md:grid-cols-3 gap-6 relative z-10">
              {/* Bonus 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center hover:bg-white/15 transition-all">
                <img src={bonusBancosQuiebra} alt="Bancos Amigos de la Quiebra" className="w-32 h-32 mx-auto object-contain mb-4 rounded-lg" loading="lazy" decoding="async" />
                <h4 className="font-bold text-lg mb-2">Bancos Amigos de la Quiebra</h4>
                <p className="text-white/80 text-sm mb-2">
                  Lista de Aprobación Garantizada con los bancos que SÍ aprueban después de bancarrota.
                </p>
                <p className="text-orange-300 font-bold">Valor: $47 - GRATIS</p>
              </div>

              {/* Bonus 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center hover:bg-white/15 transition-all">
                <img alt="Limpieza de Rastro Público" className="w-32 h-32 mx-auto object-contain mb-4 rounded-lg" src="/lovable-uploads/cd1c0ccd-284e-472a-8c74-31d3f1416e64.png" loading="lazy" decoding="async" />
                <h4 className="font-bold text-lg mb-2">Limpieza de Rastro Público</h4>
                <p className="text-white/80 text-sm mb-2">
                  Estrategias para borrar tu huella digital negativa de forma legal y efectiva.
                </p>
                <p className="text-orange-300 font-bold">Valor: $67 - GRATIS</p>
              </div>

              {/* Bonus 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center hover:bg-white/15 transition-all">
                <img alt="El Escudo Legal" className="w-32 h-32 mx-auto object-contain mb-4 rounded-lg" src="/lovable-uploads/82c48c52-e324-4771-8e04-5f4519aa81aa.png" loading="lazy" decoding="async" />
                <h4 className="font-bold text-lg mb-2">El Escudo Legal</h4>
                <p className="text-white/80 text-sm mb-2">
                  Guía de Protección Jurídica para blindar tu recuperación financiera.
                </p>
                <p className="text-orange-300 font-bold">Valor: $97 - GRATIS</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <p className="text-lg mb-2 text-sidebar-foreground font-extrabold">Precio Regular:</p>
            <p className="text-4xl font-bold text-gray-400 line-through">$97</p>
          </div>
          
          <div className="mb-8">
            <p className="text-orange-600 text-lg font-medium mb-2">Precio Especial de Hoy:</p>
            <p className="text-6xl md:text-7xl font-black bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 bg-clip-text text-transparent">
              $14.97
            </p>
            <p className="mt-2 text-muted-foreground text-xl font-extrabold">Pago único • Acceso de por vida</p>
          </div>

          {/* CTA ya está abajo, widget movido al footer */}

          {/* YES Button */}
          <Button
            asChild
            className="w-full md:w-auto bg-gradient-to-r from-teal-500 via-cyan-400 to-amber-500 hover:from-teal-600 hover:via-cyan-500 hover:to-amber-600 text-slate-900 font-bold text-sm sm:text-base md:text-xl py-5 sm:py-7 px-6 sm:px-8 md:px-12 h-auto rounded-xl shadow-[0_0_40px_rgba(20,184,166,0.4)] hover:shadow-[0_0_60px_rgba(20,184,166,0.6)] transition-all duration-300 transform hover:scale-105 animate-pulse"
          >
            <a href="/gracias" className="flex flex-col sm:flex-row items-center justify-center gap-2">
              <Flame className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
              <span className="whitespace-normal text-center leading-tight">
                SÍ, QUIERO BORRAR EL PASADO - $14.97
              </span>
            </a>
          </Button>
          <p className="text-sm mt-3 text-primary-foreground">Agregar a mi orden ahora</p>

          {/* NO Link */}
          <div className="mt-8">
            <a href="/bundle" className="text-sm underline transition-colors leading-relaxed text-primary-foreground">
              No gracias, acepto vivir con el estigma de la bancarrota por los próximos 7 a 10 años.
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
              Personas Que Renacieron Después de la Quiebra
            </h2>
            <p className="text-gray-600">Historias reales de transformación financiera</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <Quote className="w-8 h-8 text-orange-300 mb-4" />
                <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-orange-400 fill-orange-400" />)}
                  </div>
                </div>
                <div className="mt-3">
                  <p className="font-bold text-gray-800">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.location}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-12 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-gradient-to-br from-blue-50 to-orange-50 rounded-2xl p-8 border border-blue-200">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Garantía de Renacimiento</h3>
            <p className="text-gray-600 text-sm">
              30 días de garantía de devolución. Si no comienzas a ver el camino hacia tu recuperación, te devolvemos cada centavo.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-gray-500 mb-6">
            © {new Date().getFullYear()} Universo Digital Academy. Todos los derechos reservados.
          </p>
        </div>

        {/* Hotmart Sales Funnel Widget (full-bleed) */}
        <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen">
          <div id="hotmart-sales-funnel-downsell" className="w-full" />
        </div>
      </footer>
    </main>;
};
export default Downsell;